import { useEffect, useMemo, useRef, useState } from 'react';

import {
  ZfBanner,
  ZfBox,
  ZfDialog,
  ZfDialogContent,
  ZfDialogFooter,
  ZfDialogHeader,
  ZfDivider,
  ZfIcon,
  ZfLoadingSpinner,
  ZfStack,
  ZfTextButton,
  ZfTypography,
} from '@zafire/react';
import { Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { createPortfolio, updatePortfolio } from 'services/portfolios/portfolioService';
import { closeModal } from '../../reducers/modalReducer';
import PortfolioCreationSchema from './portfolioCreationSchema';
import { PortfolioTypeDependentField, ServicingRoleField } from './portfolioTypeDependentFields';
import { getFieldValues, isServicingRoleVisible, portfolioTypesForFlow } from './portfolioTypeFieldRules';
import { withoutFormOnlyFields } from './withoutFormOnlyFields';
import PortfolioDetailSection from './PortfolioDetailSection';
import { CreatePortfolioData, PortfolioDataResponse } from 'types/portfolios/portfolioData';
import useGetPortfolioBySA from './hooks/useGetPortfolioBySA';
import { AlternateInvestmentStatus } from 'types';
import { getServiceAgreementDetailsQueryKey, useGetServiceAgreementDetails } from '../../hooks/queries/privateBanking';
import { useTranslation } from 'react-i18next';
import { PortfolioCreationFlow, PortfolioType, ResponseError } from 'pco-product-service-internal-consumer';
import useGetFeatureFlags from './hooks/useGetFeatureFlags';
import { FeatureFlagName } from 'services/portfolios/portfolioService';
import useGetReferenceCurrency from './hooks/useGetReferenceCurrency';
import { useGetPortfolioRules } from './hooks/useGetPortfolioRules';
import { useQueryClient } from '@tanstack/react-query';
import { BannerErrorDialog } from './BannerErrorDialog';
import {
  InvestmentProductStatus,
  useModularOfferingFastTrackElement,
} from '../../hooks/useModularOfferingFastTrackElement';

type AddPortfolioDialogType =
  | {
      serviceAgreementId: string;
      portfolio?: PortfolioDataResponse;
      brUid?: string;
      onProceed?: () => void;
    }
  | {
      portfolio: PortfolioDataResponse;
      serviceAgreementId?: string;
      brUid?: string;
      onProceed?: () => void;
    }
  | {
      brUid: string;
      serviceAgreementId?: undefined;
      portfolio?: undefined;
      onProceed?: () => void;
    };

export type CashAccount = {
  currency: string;
  accountType: string;
};

export type PortfolioForm = {
  referenceCurrency: string;
  portfolioType: PortfolioType;
  portfolioTypeNumber: string;
  sequenceNumber: string;
  rubrik: string;
  manual: boolean;
  allocatedBo: string;
  specialBoEnabled: boolean;
  cashAccounts: CashAccount[];
  chargingCurrency: string;
} & Record<PortfolioTypeDependentField, string> &
  Record<ServicingRoleField, string>;

const LoadingSpinner = () => (
  <ZfBox style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 226 }}>
    <ZfLoadingSpinner size="medium" />
  </ZfBox>
);

const AddPortfolioDialog = ({ brUid, serviceAgreementId, onProceed, portfolio }: AddPortfolioDialogType) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [errorObject, setErrorObject] = useState<Error | undefined>(undefined);
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [showInvestmentDialog, setShowInvestmentDialog] = useState(false);
  const [dismissBanner, setDismissBanner] = useState(false);
  const status = useModularOfferingFastTrackElement(!!serviceAgreementId);
  const investmentProductRef = useRef<HTMLElement>(null);
  const { data: featureFlags } = useGetFeatureFlags();
  const rocEnabled = featureFlags?.find((item) => item.name === FeatureFlagName.ROC_ENABLED)?.enabled ?? false;

  const { data: serviceAgreementData, isLoading: retrievingServiceAgreement } =
    useGetServiceAgreementDetails(serviceAgreementId);

  const isLoadPortfolioData = useMemo(() => {
    if (
      serviceAgreementData &&
      serviceAgreementData.agreementStatus !== AlternateInvestmentStatus.READY_FOR_CONFIGURATION
    ) {
      return true;
    }
    return false;
  }, [serviceAgreementData]);

  const { data: portfolioData, isLoading: retrievingPortfolioData } = useGetPortfolioBySA(
    serviceAgreementId || '',
    isLoadPortfolioData,
  );

  const isStandaloneCreate = !portfolio && !serviceAgreementId;

  const isCreatingPortfolio =
    serviceAgreementData?.agreementStatus === AlternateInvestmentStatus.READY_FOR_CONFIGURATION || isStandaloneCreate;

  const creationFlow = isStandaloneCreate ? PortfolioCreationFlow.Standalone : PortfolioCreationFlow.ServiceAgreement;

  const { data: referenceCurrencyData, isLoading: retrievingReferenceCurrency } = useGetReferenceCurrency(
    brUid || '',
    isCreatingPortfolio,
  );

  const { data: portfolioRules, isLoading: retrievingPortfolioRules } = useGetPortfolioRules(
    brUid || '',
    isCreatingPortfolio,
  );

  const isLoading = useMemo(() => {
    if (retrievingServiceAgreement) {
      return true;
    }
    if (isLoadPortfolioData) {
      return retrievingPortfolioData;
    }
    return false;
  }, [isLoadPortfolioData, retrievingPortfolioData, retrievingServiceAgreement]);

  const retrievingCreationData = retrievingPortfolioRules || retrievingReferenceCurrency;

  const isEdit = useMemo(() => {
    if (portfolio) {
      return true;
    }
    if (
      serviceAgreementData &&
      serviceAgreementData.agreementStatus !== AlternateInvestmentStatus.READY_FOR_CONFIGURATION
    ) {
      return true;
    }
    return false;
  }, [serviceAgreementData, portfolio]);

  const initialValues = useMemo(() => {
    if (portfolio) {
      return {
        productCurrency: '',
        referenceCurrency: portfolio.referenceCurrency || '',
        portfolioType: portfolio.portfolioType,
        portfolioTypeNumber: portfolio.portfolioNumber,
        sequenceNumber: portfolio.portfolioNumber.slice(-2),
        chargingCurrency: portfolio.chargingCurrency,
        rubrik: portfolio.rubrik,
        details: portfolio.details,
        allocatedBo: portfolio.allocatedBo,
        specialBoEnabled: portfolio.specialBoEnabled ?? !!portfolio.allocatedBo,
        cashAccounts: [],
        servicingRoleType: '',
        servicingUser: '',
      };
    }
    if (
      serviceAgreementData &&
      portfolioData &&
      serviceAgreementData.agreementStatus !== AlternateInvestmentStatus.READY_FOR_CONFIGURATION
    ) {
      return {
        productCurrency: '',
        referenceCurrency: portfolioData.referenceCurrency || '',
        portfolioType: portfolioData.portfolioType || '',
        portfolioTypeNumber: portfolioData.portfolioNumber || '',
        sequenceNumber: portfolioData?.portfolioNumber?.slice(-2) || '',
        chargingCurrency: portfolioData.chargingCurrency || '',
        rubrik: portfolioData.rubrik || '',
        allocatedBo: portfolioData.allocatedBo || '',
        specialBoEnabled: portfolioData.specialBoEnabled ?? !!portfolioData.allocatedBo,
        cashAccounts: portfolioData.cashAccounts?.map((account) => ({
          ...account,
          currency: account.currency || '',
          accountType: account.accountType || '',
        })) || [{ currency: '', accountType: '' }],
        servicingRoleType: '',
        servicingUser: '',
      };
    }

    const defaultPortfolioType =
      portfolioTypesForFlow(portfolioRules ?? [], creationFlow)[0] ?? (isStandaloneCreate ? '' : PortfolioType.Custody);

    return {
      productCurrency: '',
      referenceCurrency: referenceCurrencyData?.referenceCurrency || '',
      portfolioType: defaultPortfolioType,
      portfolioTypeNumber: '',
      sequenceNumber: '',
      rubrik: '',
      ...getFieldValues({
        rules: portfolioRules ?? [],
        portfolioType: defaultPortfolioType,
        isStandaloneCreate: true,
        isEdit: false,
      }),
      manual: false,
      allocatedBo: '',
      specialBoEnabled: false,
      cashAccounts: [{ currency: referenceCurrencyData?.referenceCurrency || '', accountType: '2120' }],
      chargingCurrency: referenceCurrencyData?.chargingCurrency || '',
      servicingRoleType: '',
      servicingUser: '',
    };
  }, [
    portfolio,
    portfolioData,
    serviceAgreementData,
    referenceCurrencyData,
    portfolioRules,
    creationFlow,
    isStandaloneCreate,
  ]);

  const dispatch = useDispatch();

  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleClose = () => {
    dispatch(closeModal());
  };

  const productDetail = useMemo(() => {
    if (isLoading) {
      return <LoadingSpinner />;
    }

    if (serviceAgreementId && serviceAgreementData) {
      const displayedServiceModel = serviceAgreementData.draftServiceModel ?? serviceAgreementData.serviceModel;
      return (
        <>
          <ZfStack direction="row" spacing="small">
            <ZfTypography variant="label-medium" color="emphasis" weight="bold">
              {displayedServiceModel?.productDisplayName}
            </ZfTypography>
            <ZfDivider style={{ flex: 1, paddingTop: 'var(--padding-top-xs)' }} />
          </ZfStack>
          <ZfBox
            style={{
              backgroundColor: 'var(--bg-color-default-muted, #F8F9FB)',
              padding: 'var(--gap-horizontal-md, 16px)',
            }}
          >
            <ZfStack direction="row" spacing="medium">
              <ZfStack style={{ flex: 1 }} spacing="extra-small">
                <ZfStack direction="row" justify="space-between">
                  <ZfTypography>{t('pb.investmentproduct.description.engagementtype')}</ZfTypography>
                  <ZfTypography>{displayedServiceModel?.engagementTypeName ?? '-'}</ZfTypography>
                </ZfStack>
                <ZfDivider />
                <ZfStack direction="row" justify="space-between">
                  <ZfTypography>{t('pb.investmentproduct.description.servicemodel')}</ZfTypography>
                  <ZfTypography>{displayedServiceModel?.serviceModelName ?? '-'}</ZfTypography>
                </ZfStack>
                <ZfDivider />

                <ZfStack direction="row" justify="space-between">
                  <ZfTypography>{t('pb.investmentproduct.description.productline')}</ZfTypography>
                  <ZfTypography>{displayedServiceModel?.productLineName ?? '-'}</ZfTypography>
                </ZfStack>
                <ZfDivider />
                <ZfStack direction="row" justify="space-between">
                  <ZfTypography>{t('pb.investmentproduct.description.productimplementation')}</ZfTypography>
                  <ZfTypography>{displayedServiceModel?.productImplementationName ?? '-'}</ZfTypography>
                </ZfStack>
              </ZfStack>
              <ZfStack style={{ flex: 1 }} spacing="extra-small">
                <ZfStack direction="row" justify="space-between">
                  <ZfTypography>{t('pb.investmentproduct.description.investmentstrategy')}</ZfTypography>
                  <ZfTypography>{displayedServiceModel?.investmentStrategyName ?? '-'}</ZfTypography>
                </ZfStack>
                <ZfDivider />
                <ZfStack direction="row" justify="space-between">
                  <ZfTypography>{t('pb.investmentproduct.description.bankingservicepackage')}</ZfTypography>
                  {displayedServiceModel?.bankingServicePackageName ?? '-'}
                </ZfStack>
                <ZfDivider />
                <ZfStack direction="row" justify="space-between">
                  <ZfTypography>{t('pb.investmentproduct.description.transactionfeemodel')}</ZfTypography>
                  <ZfTypography>{displayedServiceModel?.transactionFeeModelName ?? '-'}</ZfTypography>
                </ZfStack>
                <ZfDivider />
                <ZfStack direction="row" justify="space-between">
                  <ZfTypography>{t('pb.investmentproduct.description.currency')}</ZfTypography>
                  <ZfTypography>{displayedServiceModel?.currencyCode}</ZfTypography>
                </ZfStack>
              </ZfStack>
            </ZfStack>
          </ZfBox>
          <ZfStack direction="row" justify="flex-end">
            {(status === InvestmentProductStatus.LOADING || status === InvestmentProductStatus.ERROR) && (
              <ZfTextButton hierarchy="secondary" disabled>
                <ZfIcon slot="leading" name="sync" size="medium" />
                {t('pb.investmentproduct.changeproduct')}
              </ZfTextButton>
            )}
            {status === InvestmentProductStatus.DONE && (
              <ZfTextButton hierarchy="secondary" onClick={() => setShowInvestmentDialog(true)}>
                <ZfIcon slot="leading" name="sync" size="medium" />
                {t('pb.investmentproduct.changeproduct')}
              </ZfTextButton>
            )}
          </ZfStack>
        </>
      );
    }
    return null;
  }, [serviceAgreementId, serviceAgreementData, isLoading, t, status]);

  useEffect(() => {
    if (!serviceAgreementData || status !== InvestmentProductStatus.DONE) return;
    const element = investmentProductRef.current;
    if (!element) return;

    const handleSubmitted = async () => {
      await queryClient.invalidateQueries({ queryKey: getServiceAgreementDetailsQueryKey(serviceAgreementId) });
      setShowInvestmentDialog(false);
    };

    const handleCancel = () => {
      setShowInvestmentDialog(false);
    };

    element.addEventListener('FAST_TRACK_SUBMITTED', handleSubmitted);
    element.addEventListener('FAST_TRACK_CANCELLED', handleCancel);

    return () => {
      element.removeEventListener('FAST_TRACK_SUBMITTED', handleSubmitted);
      element.removeEventListener('FAST_TRACK_CANCELLED', handleCancel);
    };
  }, [queryClient, serviceAgreementData, serviceAgreementId, status]);

  const headerLabel = useMemo(() => {
    if (portfolio) {
      return t('pb.portfolio.editheader.title');
    }
    if (isStandaloneCreate) {
      return t('pb.portfolio.addheader.title');
    }
    if (
      serviceAgreementData &&
      serviceAgreementData.agreementStatus !== AlternateInvestmentStatus.READY_FOR_CONFIGURATION
    ) {
      return t('pb.portfolio.configuration.title');
    }
    return t('pb.portfolio.header.title');
  }, [portfolio, isStandaloneCreate, serviceAgreementData, t]);

  return (
    <Formik
      key={retrievingCreationData ? 'retrieving-creation-data' : 'creation-data-retrieved'}
      validateOnBlur={false}
      validateOnChange
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={PortfolioCreationSchema(isEdit, isStandaloneCreate, rocEnabled, portfolioRules)}
      onSubmit={async (data, { setSubmitting, resetForm }) => {
        setSubmitting(true);
        setShowErrorBanner(false);
        setErrorObject(undefined);
        const { servicingRoleType, servicingUser } = data;
        const portfolioValues = {
          ...withoutFormOnlyFields(data),
          sequenceNumber: Number(data.sequenceNumber),
          servicingRole:
            isServicingRoleVisible({
              rules: portfolioRules ?? [],
              portfolioType: String(data.portfolioType),
              isStandaloneCreate,
              rocEnabled,
            }) &&
            (servicingRoleType || servicingUser)
              ? { servicingRoleType, servicingUser }
              : null,
        };
        try {
          if (
            serviceAgreementId &&
            serviceAgreementData?.agreementStatus === AlternateInvestmentStatus.READY_FOR_CONFIGURATION
          ) {
            await createPortfolio({
              serviceAgreementId: serviceAgreementId,
              ...(portfolioValues as unknown as CreatePortfolioData),
              businessRelationshipId: brUid || '',
            });
            await queryClient.invalidateQueries({ queryKey: getServiceAgreementDetailsQueryKey(serviceAgreementId) });
          } else if (serviceAgreementId) {
            await updatePortfolio({
              ...portfolioData,
              ...(portfolioValues as unknown as PortfolioDataResponse),
              serviceAgreementId,
            });
          } else if (portfolio) {
            await updatePortfolio({ ...portfolio, ...(portfolioValues as unknown as PortfolioDataResponse) });
            await queryClient.invalidateQueries({ queryKey: ['useGetPortfolioHistory', portfolio.portfolioId] });
          } else {
            await createPortfolio({
              ...(portfolioValues as unknown as CreatePortfolioData),
              businessRelationshipId: brUid || '',
            });
          }
          resetForm();
          setSubmitting(false);
          await queryClient.invalidateQueries({ queryKey: ['useGetPortfolioBySA', serviceAgreementId] });
          await queryClient.invalidateQueries({ queryKey: ['useGetSequenceNumber'] });
          if (onProceed) {
            onProceed();
          }
          handleClose();
        } catch (e: unknown) {
          if (e instanceof ResponseError) {
            let message = t('pb.portfolio.error.unknown');

            try {
              const errorBody = await e.response.json();
              message = errorBody.title ?? errorBody.message ?? message;
              setErrorObject(errorBody);
            } catch {
              message = await e.response.text();
              setErrorObject(e instanceof Error ? e : new Error(String(e)));
            }

            setErrorMsg(message);
          } else if (e instanceof Error) {
            setErrorMsg(e.message);
            setErrorObject(e);
          } else {
            setErrorMsg(t('pb.portfolio.error.unknown'));
          }

          setShowErrorBanner(true);
          setSubmitting(false);
        }
      }}
    >
      {({ submitForm, resetForm, isSubmitting, values }) => {
        const showBanner =
          !!serviceAgreementData?.serviceModel &&
          values.referenceCurrency !== '' &&
          serviceAgreementData.serviceModel.currencyCode !== values.referenceCurrency;

        return (
          <>
            <BannerErrorDialog
              title={errorMsg ?? t('pb.portfolio.error.database.title')}
              showDialog={showErrorDialog}
              onDialogClose={() => setShowErrorDialog(false)}
              error={errorObject}
            />
            {serviceAgreementId && serviceAgreementData && status === InvestmentProductStatus.DONE && (
              <iap-modular-offering-fast-track-element
                brUid={brUid || ''}
                showDialog={showInvestmentDialog}
                serviceAgreementId={serviceAgreementId}
                ref={investmentProductRef}
              />
            )}
            <ZfDialog
              show={!showErrorDialog && !showInvestmentDialog}
              size="medium"
              onDialogClose={handleClose}
              disableLightDismiss
            >
              <ZfDialogHeader>{headerLabel}</ZfDialogHeader>
              <ZfDialogContent>
                <ZfStack spacing="medium">
                  {showErrorBanner && (
                    <ZfBanner
                      type="error"
                      dismissible={false}
                      actionButtonText={t('pb.portfolio.error.details')}
                      onBannerActionButtonClick={() => setShowErrorDialog(true)}
                    >
                      <ZfStack spacing="extra-extra-small">
                        <ZfTypography variant="label-medium" color="emphasis" weight="bold">
                          {t('pb.portfolio.error.title')}
                        </ZfTypography>
                        <ZfTypography variant="label-small" color="emphasis">
                          {t('pb.portfolio.error.banner.message')}
                        </ZfTypography>
                        <ZfTypography variant="label-small" color="emphasis">
                          {t('pb.portfolio.error.banner.support')}
                        </ZfTypography>
                      </ZfStack>
                    </ZfBanner>
                  )}
                  {showBanner && !dismissBanner && (
                    <ZfBanner
                      type="warning"
                      onBannerDismiss={() => setDismissBanner(true)}
                      description={t('pb.portfolio.currencydifferent', {
                        currency: serviceAgreementData?.serviceModel?.currencyCode,
                      })}
                    ></ZfBanner>
                  )}

                  {productDetail}
                  {retrievingCreationData ? (
                    <LoadingSpinner />
                  ) : (
                    <PortfolioDetailSection
                      brUid={brUid || ''}
                      serviceAgreementId={serviceAgreementId}
                      isLoading={isLoading}
                      portfolio={portfolio}
                      isStandaloneCreate={isStandaloneCreate}
                      isEdit={isEdit}
                      portfolioRules={portfolioRules}
                      onCloseServiceAgreementError={(message, error) => {
                        setErrorMsg(message);
                        setErrorObject(error);
                        setShowErrorDialog(true);
                      }}
                    />
                  )}
                </ZfStack>
              </ZfDialogContent>
              <ZfDialogFooter>
                <ZfStack slot="primary" direction="row" spacing="small">
                  <ZfTextButton
                    size="medium"
                    hierarchy="tertiary"
                    onClick={() => {
                      handleClose();
                      resetForm();
                    }}
                  >
                    {t('pb.investmentproduct.cancel')}
                  </ZfTextButton>
                  <ZfTextButton
                    size="medium"
                    onClick={async () => {
                      if (!isSubmitting) {
                        await submitForm();
                      }
                    }}
                    disabled={isSubmitting || (isCreatingPortfolio && values.portfolioTypeNumber === '')}
                  >
                    {isSubmitting && <ZfLoadingSpinner size="small" />}
                    {t('pb.investmentproduct.save')}
                  </ZfTextButton>
                </ZfStack>
              </ZfDialogFooter>
            </ZfDialog>
          </>
        );
      }}
    </Formik>
  );
};

export default AddPortfolioDialog;
